import React, { useEffect, useRef, useState } from 'react';
import { AVATAR_COLORS } from './Avatar';
import { Eraser, Trash2, Save, RotateCcw } from 'lucide-react';

// v2.1 AVATAR DRAWING MODE — freeform canvas painting.
// Emits data URL PNG via onSave(dataUrl) when SAVE clicked. Also emits null via onClear().

const CANVAS_SIZE = 256; // internal resolution; scales visually via CSS
const BRUSH_MIN = 2;
const BRUSH_MAX = 24;

export default function DrawingCanvas({ initialDataUrl = null, onSave, onClear }) {
  const canvasRef = useRef(null);
  const [color, setColor] = useState('#39ff14');
  const [brush, setBrush] = useState(6);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEraser, setIsEraser] = useState(false);
  const [history, setHistory] = useState([]); // stack of image data URLs (undo)

  // Setup canvas on mount + load initial drawing
  const [_isDirty, setIsDirty] = useState(false); // v2.1 track whether any stroke was made
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    if (initialDataUrl && initialDataUrl.startsWith('data:image')) {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE); setIsDirty(true); };
      img.src = initialDataUrl;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const snapshot = () => {
    const c = canvasRef.current;
    if (!c) return;
    setHistory((h) => [...h.slice(-19), c.toDataURL('image/png')]);
  };

  const pointer = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    const x = ((t.clientX - rect.left) / rect.width) * CANVAS_SIZE;
    const y = ((t.clientY - rect.top) / rect.height) * CANVAS_SIZE;
    return { x, y };
  };

  const startDraw = (e) => {
    e.preventDefault();
    snapshot();
    setIsDirty(true);
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const p = pointer(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const p = pointer(e);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brush;
    ctx.strokeStyle = isEraser ? '#0a0a0a' : color;
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const endDraw = () => setIsDrawing(false);

  const undo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
    };
    img.src = last;
  };

  const clearCanvas = () => {
    snapshot();
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  };

  const doSave = () => {
    const c = canvasRef.current;
    // v2.1 polish: if user never drew, don't persist a blank canvas
    if (!_isDirty) {
      onClear && onClear();
      return;
    }
    const dataUrl = c.toDataURL('image/png');
    onSave && onSave(dataUrl);
  };

  const doRemoveDrawing = () => {
    clearCanvas();
    onClear && onClear();
  };

  return (
    <div className="w-full max-w-md mx-auto flex flex-col items-center gap-3" data-testid="drawing-canvas-wrapper">
      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        className="drawing-canvas brutal-border"
        data-testid="drawing-canvas"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
        style={{
          width: 256, height: 256,
          cursor: isEraser ? 'cell' : 'crosshair',
          background: '#0a0a0a',
          touchAction: 'none',
        }}
      />

      {/* Color palette */}
      <div className="flex flex-wrap gap-2 justify-center" data-testid="drawing-color-picker">
        {AVATAR_COLORS.map((c) => (
          <button
            key={c.key}
            onClick={() => { setColor(c.hex); setIsEraser(false); }}
            className="drawing-swatch"
            data-testid={`drawing-color-${c.key}`}
            data-selected={color === c.hex && !isEraser ? 'true' : 'false'}
            title={c.name}
            style={{
              width: 28, height: 28,
              background: c.hex,
              border: `2px solid ${color === c.hex && !isEraser ? '#fff' : '#333'}`,
              boxShadow: color === c.hex && !isEraser ? `0 0 8px ${c.hex}` : 'none',
              borderRadius: 2,
            }}
          />
        ))}
      </div>

      {/* Brush size */}
      <div className="flex items-center gap-3 text-xs uppercase font-mono text-[color:var(--text-dim)]" data-testid="drawing-brush-controls">
        <span>BRUSH</span>
        <input
          type="range"
          min={BRUSH_MIN}
          max={BRUSH_MAX}
          value={brush}
          onChange={(e) => setBrush(parseInt(e.target.value, 10))}
          className="w-32"
          data-testid="drawing-brush-slider"
        />
        <span className="text-[color:var(--neon)] w-6 text-right">{brush}px</span>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 justify-center">
        <button
          onClick={() => setIsEraser((v) => !v)}
          className="brutal-btn text-xs px-3 py-2"
          data-testid="drawing-eraser"
          data-active={isEraser ? 'true' : 'false'}
          style={{ borderColor: isEraser ? '#ff007f' : undefined, color: isEraser ? '#ff007f' : undefined }}
        >
          <Eraser size={14}/> {isEraser ? 'ERASING' : 'ERASER'}
        </button>
        <button
          onClick={undo}
          className="brutal-btn text-xs px-3 py-2"
          data-testid="drawing-undo"
          disabled={history.length === 0}
        >
          <RotateCcw size={14}/> UNDO
        </button>
        <button
          onClick={clearCanvas}
          className="brutal-btn text-xs px-3 py-2"
          data-testid="drawing-clear"
        >
          <Trash2 size={14}/> CLEAR
        </button>
        <button
          onClick={doSave}
          className="brutal-btn text-xs px-3 py-2"
          data-testid="drawing-save"
          style={{ borderColor: 'var(--neon)' }}
        >
          <Save size={14}/> USE DRAWING
        </button>
        <button
          onClick={doRemoveDrawing}
          className="brutal-btn text-xs px-3 py-2"
          data-testid="drawing-remove"
          title="Discard drawing and go back to emoji avatar"
        >
          REMOVE
        </button>
      </div>
    </div>
  );
}
